import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  TrendingUp, RefreshCw, Loader2, CalendarDays, ChevronDown,
  ChevronUp, Plus, Pencil, Trash2, AlertCircle, Check, X,
  BarChart3, Package,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from "recharts";
import { useAuth } from "@/lib/auth";

const DAYS_ES = ["Lun", "Mar", "Mier", "Jue", "Vier", "Sab", "Dom"];
const CATEGORY_COLOR = {
  masas:    "bg-amber-100 text-amber-800 border-amber-200",
  salsas:   "bg-red-100 text-red-800 border-red-200",
  carnes:   "bg-rose-100 text-rose-800 border-rose-200",
  panes:    "bg-yellow-100 text-yellow-800 border-yellow-200",
  verduras: "bg-green-100 text-green-800 border-green-200",
};
const CONFIDENCE_COLOR = {
  alta:  "text-green-600",
  media: "text-amber-600",
  baja:  "text-red-500",
  // Fix WH32 (2026-09-07): un ratio que nadie calibro no es "confianza baja",
  // es otra cosa. En gris, para que no compita con los estados que si miden
  // calidad del dato.
  sin_calibrar: "text-slate-400",
};
const CONFIDENCE_LABEL = {
  alta: "Conf. alta",
  media: "Conf. media",
  baja: "Conf. baja",
  sin_calibrar: "Sin calibrar",
};
const EVENT_TYPE_LABEL = {
  feriado_nacional: "Feriado",
  evento_local: "Evento local",
  promocion: "Promocion",
  clausura: "Clausura",
};

function fmt(n) { return n != null ? Number(n).toLocaleString("es-MX") : "—"; }
function fmtMXN(n) { return n != null ? `$${Number(n).toLocaleString("es-MX", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : "—"; }

function ForecastCard({ forecast, label }) {
  const pm = forecast?.predicted_metrics || {};
  const isInsuf = pm.insufficient_data;
  const accuracy = forecast?.accuracy_score;

  return (
    <div className={`bg-white rounded-xl border p-4 ${isInsuf ? "border-slate-200 opacity-75" : "border-slate-200"}`}>
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{label}</div>
      {isInsuf ? (
        <div className="flex items-center gap-1.5 text-sm text-slate-400">
          <AlertCircle className="w-4 h-4" />
          <span>Datos insuficientes</span>
        </div>
      ) : (
        <>
          {/* Fix WH27 (2026-09-04): la venta pasa a ser el numero grande. Es lo
              que Claudia pidio ver al abrir el sistema ("la proyeccion de venta
              del dia") y es lo unico que hoy sale de un dato real del POS. Las
              ordenes bajan a linea secundaria y, mientras Wansoft no informe el
              conteo de tickets, dicen "sin dato" en vez de 0. */}
          <div className="text-3xl font-bold text-slate-900">{fmtMXN(pm.total_sales)}</div>
          <div className="text-xs text-slate-500 mt-0.5">venta estimada</div>
          <div className="text-xs text-slate-400 mt-1">
            Banda: {fmtMXN(pm.total_sales_low)} – {fmtMXN(pm.total_sales_high)}
          </div>
          {pm.orders_unavailable ? (
            <div className="mt-2 text-xs text-slate-400" title={pm.orders_note || ""}>
              Ordenes: <span className="font-medium">sin dato</span>
            </div>
          ) : (
            <div className="flex items-center gap-3 mt-2 text-xs text-slate-600">
              <span className="font-medium">{fmt(pm.total_orders)} ordenes</span>
              <span>ticket {fmtMXN(pm.ticket_avg)}</span>
            </div>
          )}
          {pm.holiday_name && (
            <div className="mt-2 text-xs bg-amber-50 border border-amber-200 rounded px-2 py-1 text-amber-700">
              Ajuste por {pm.holiday_name} (x{pm.holiday_applied ? "ajustado" : "—"})
            </div>
          )}
          {/* Fix WH38 (2026-09-09): corrección por tendencia semanal. Sólo se
              muestra si de verdad se aplicó algo (factor != 1.0) — sin dato
              suficiente el factor queda en 1.0 y no hay nada que anunciar. */}
          {pm.trend_factor != null && pm.trend_factor !== 1.0 && (
            <div
              data-testid="forecast-trend-badge"
              className={`mt-2 text-xs rounded px-2 py-1 border ${
                pm.trend_weekly_pct < 0
                  ? "bg-red-50 border-red-200 text-red-700"
                  : "bg-emerald-50 border-emerald-200 text-emerald-700"
              }`}
              title={`Tendencia calculada sobre ${pm.trend_source} (${pm.trend_days_used} dias comparables)` +
                     (pm.trend_last_day ? `, con datos hasta el ${pm.trend_last_day}` : "")}
            >
              Tendencia: {pm.trend_weekly_pct > 0 ? "+" : ""}{pm.trend_weekly_pct}%/semana
              {" "}(ajuste x{pm.trend_factor})
              {pm.trend_clamped && <span className="ml-1 text-slate-500">· tope ±20%</span>}
            </div>
          )}
          {accuracy != null && (
            <div className="mt-2 text-xs text-slate-500">
              Accuracy: <span className={`font-semibold ${accuracy >= 0.8 ? "text-green-600" : accuracy >= 0.6 ? "text-amber-600" : "text-red-500"}`}>
                {(accuracy * 100).toFixed(0)}%
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function HolidayForm({ event, onClose, onSaved, branches }) {
  const isNew = !event?.id;
  const [form, setForm] = useState(isNew ? {
    branch_id: "global",
    date: "",
    type: "evento_local",
    name: "",
    expected_impact: "neutral",
    multiplier: 1.0,
    active: true,
  } : { ...event });
  const [saving, setSaving] = useState(false);

  const upd = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const handleSave = async () => {
    if (!form.name.trim() || !form.date) { toast.error("Nombre y fecha son obligatorios"); return; }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/ops/holidays-events", form);
        toast.success("Evento creado");
      } else {
        await api.put(`/ops/holidays-events/${event.id}`, {
          name: form.name, type: form.type, expected_impact: form.expected_impact,
          multiplier: form.multiplier, active: form.active,
        });
        toast.success("Evento actualizado");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h2 className="font-bold text-slate-900">{isNew ? "Nuevo evento / feriado" : "Editar evento"}</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-400"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Nombre *</label>
            <input value={form.name} onChange={e => upd("name", e.target.value)}
              placeholder="Navidad, Promo verano..."
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Fecha *</label>
              <input type="date" value={form.date} onChange={e => upd("date", e.target.value)} disabled={!isNew}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] disabled:bg-slate-50" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Tipo</label>
              <select value={form.type} onChange={e => upd("type", e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]">
                {Object.entries(EVENT_TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
          {isNew && (
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Sucursal</label>
              <select value={form.branch_id} onChange={e => upd("branch_id", e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]">
                <option value="global">Todas las sucursales</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Impacto esperado</label>
              <select value={form.expected_impact} onChange={e => upd("expected_impact", e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]">
                <option value="alta_baja">Alta baja</option>
                <option value="alta_subida">Alta subida</option>
                <option value="neutral">Neutral</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Multiplicador</label>
              <input type="number" value={form.multiplier} onChange={e => upd("multiplier", parseFloat(e.target.value) || 1)}
                min={0} max={5} step={0.1}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
              <p className="text-xs text-slate-400 mt-0.5">0.3 = 30% del dia normal</p>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 pb-5">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">Cancelar</button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#FFDF00] text-sm font-semibold text-slate-900 hover:bg-yellow-300 disabled:opacity-60"
            data-testid="save-holiday-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OpsPredicciones() {
  const { user } = useAuth();
  const isAdmin = user?.role === "corporate_admin";
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [forecasts, setForecasts] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [holidays, setHolidays] = useState([]);
  // Fix WH27 (2026-09-04): guardar el status del endpoint de sugerencias para
  // poder explicar POR QUE no hay ninguna, en vez de dejar un hueco en blanco.
  const [suggestionsStatus, setSuggestionsStatus] = useState("ok");
  // Fix WH34 (2026-09-08): proyeccion por platillo y hora.
  const [prodHora, setProdHora] = useState(null);
  // Fix WH41 (2026-09-09): insumos calculados desde la receta real (WH40).
  const [insumos, setInsumos] = useState(null);
  const [verSinReceta, setVerSinReceta] = useState(false);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [showHolidays, setShowHolidays] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);

  const loadBranches = useCallback(async () => {
    try {
      const res = await api.get("/branches");
      const bs = res.data.filter(b => !b.is_corporate);
      setBranches(bs);
      if (!selectedBranch && bs.length > 0) {
        const myBranch = user?.branch_id || user?.branches_authorized?.[0];
        const match = bs.find(b => b.id === myBranch) || bs[0];
        setSelectedBranch(match.id);
      }
    } catch { /* ignore */ }
  }, [user, selectedBranch]);

  const loadData = useCallback(async (branchId) => {
    if (!branchId) return;
    setLoading(true);
    try {
      const today = new Date();
      const from = new Date(today); from.setDate(today.getDate() + 1);
      const to = new Date(today); to.setDate(today.getDate() + 7);
      const toStr = to.toISOString().split("T")[0];
      const fromStr = from.toISOString().split("T")[0];

      const [fRes, sRes, hRes] = await Promise.all([
        api.get(`/ops/forecasts?branch_id=${branchId}&from=${fromStr}&to=${toStr}`),
        api.get(`/ops/suggestions/today?branch_id=${branchId}`),
        api.get(`/ops/holidays-events?branch_id=${branchId}&year=${today.getFullYear()}`),
      ]);
      setForecasts(fRes.data);
      setSuggestions(sRes.data.suggestions || []);
      setSuggestionsStatus(sRes.data.status || "ok");   // Fix WH27 (2026-09-04)
      setHolidays(hRes.data);
      // Fix WH34 (2026-09-08): va aparte y no bloquea el resto — si falla, la
      // pantalla se dibuja igual con todo lo demas.
      try {
        const pRes = await api.get(`/ops/forecasts/production-by-hour?branch_id=${branchId}&limit=25`);
        setProdHora(pRes.data);
      } catch { setProdHora(null); }
      // Fix WH41 (2026-09-09): mismo criterio que el WH34 — va aparte, y si
      // falla la pantalla se dibuja igual sin esta sección.
      try {
        const iRes = await api.get(`/ops/forecasts/insumos?branch_id=${branchId}`);
        setInsumos(iRes.data);
      } catch { setInsumos(null); }
    } catch {
      toast.error("Error al cargar predicciones");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadBranches(); }, [loadBranches]);
  useEffect(() => { if (selectedBranch) loadData(selectedBranch); }, [selectedBranch, loadData]);

  const handleRegenerate = async () => {
    if (!selectedBranch) return;
    setRegenerating(true);
    try {
      const r = await api.post("/ops/forecasts/regenerate", { branch_id: selectedBranch, days_ahead: 7 });
      toast.success(`${r.data.forecasts_created} forecasts regenerados`);
      loadData(selectedBranch);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al regenerar");
    } finally {
      setRegenerating(false);
    }
  };

  const handleDeleteHoliday = async (id, name) => {
    if (!window.confirm(`Eliminar evento "${name}"?`)) return;
    try {
      await api.delete(`/ops/holidays-events/${id}`);
      toast.success("Evento eliminado");
      loadData(selectedBranch);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al eliminar");
    }
  };

  // Preparar datos para el gráfico
  const chartData = forecasts.map(f => {
    const pm = f.predicted_metrics || {};
    const d = new Date(f.forecast_date + "T12:00:00");
    return {
      label: DAYS_ES[d.getDay() === 0 ? 6 : d.getDay() - 1],
      date: f.forecast_date,
      ordenes: pm.insufficient_data ? 0 : (pm.total_orders || 0),
      bajo: pm.insufficient_data ? 0 : (pm.total_orders_low || 0),
      alto: pm.insufficient_data ? 0 : (pm.total_orders_high || 0),
      ventas: pm.insufficient_data ? 0 : (pm.total_sales || 0),
      insufficient: pm.insufficient_data,
    };
  });

  const tomorrow = forecasts.find(f => {
    const d = new Date(f.forecast_date + "T12:00:00");
    const t = new Date(); t.setDate(t.getDate() + 1);
    return d.toDateString() === t.toDateString();
  });

  const avgAccuracy = forecasts.filter(f => f.accuracy_score != null).reduce((acc, f, i, arr) => {
    return acc + (f.accuracy_score / arr.length);
  }, 0);

  return (
    <div className="p-6 space-y-5" data-testid="predicciones-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Predicciones de produccion</h1>
          <p className="text-sm text-slate-500">Venta real del POS Wansoft — promedio ponderado por dia de semana + ajuste de feriados</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && branches.length > 0 && (
            <select
              value={selectedBranch}
              onChange={e => setSelectedBranch(e.target.value)}
              className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
              data-testid="branch-select"
            >
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
          <button
            onClick={handleRegenerate}
            disabled={regenerating || !selectedBranch}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-60"
            data-testid="regenerate-btn"
          >
            {regenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Regenerar
          </button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="bg-white rounded-xl border border-slate-200 h-24 animate-pulse" />)}</div>
      ) : (
        <>
          {/* Info insuficiente */}
          {forecasts.length > 0 && forecasts.every(f => f.predicted_metrics?.insufficient_data) && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                Datos insuficientes para generar predicciones. Se necesitan al menos 2 cortes del mismo dia de semana.
                Las predicciones mejoran automaticamente a medida que se acumulan cortes historicos.
              </span>
            </div>
          )}

          {/* Cards: mañana + 3 dias siguientes */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {forecasts.slice(0, 4).map((f, i) => {
              const d = new Date(f.forecast_date + "T12:00:00");
              const labels = ["Manana", "Pasado", "En 3 dias", "En 4 dias"];
              return <ForecastCard key={f.id} forecast={f} label={labels[i] || f.forecast_date} />;
            })}
          </div>

          {/* Gráfico 7 días */}
          {/* Fix WH27 (2026-09-04): el grafico se dibujaba solo si alguna barra
              de ordenes era > 0 y, como todas eran 0, no aparecia nunca. Pasa a
              graficar la venta estimada, que es el dato que si existe. */}
          {chartData.some(d => d.ventas > 0) && (
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-semibold text-slate-700">Proyeccion proximos 7 dias — venta estimada</span>
                {avgAccuracy > 0 && (
                  <span className="ml-auto text-xs text-slate-400">
                    Accuracy promedio: <span className="font-semibold text-slate-600">{(avgAccuracy * 100).toFixed(0)}%</span>
                  </span>
                )}
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip
                    formatter={(value, name) => {
                      if (name === "ordenes") return [fmt(value), "Ordenes"];
                      if (name === "ventas") return [fmtMXN(value), "Ventas"];
                      return [value, name];
                    }}
                  />
                  <Bar dataKey="ventas" fill="#FFDF00" radius={[4, 4, 0, 0]} name="ventas" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Sugerencias de producción */}
          {/* Fix WH27 (2026-09-04): cuando no hay sugerencias porque falta el
              conteo de tickets, el bloque desaparecia sin decir nada y quedaba
              un hueco. Ahora explica el motivo. */}
          {suggestions.length === 0 && suggestionsStatus === "sin_conteo_de_ordenes" && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-600">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                Todavia no se puede sugerir produccion: las cantidades por categoria se
                calculan por orden y el POS no informa cuantos tickets hubo. La proyeccion
                de venta de arriba no depende de ese dato.
              </span>
            </div>
          )}
          {suggestions.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Package className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-semibold text-slate-700">Sugerencias de produccion — manana</span>
              </div>
              {/* Fix WH32 (2026-09-07): mientras ningun ratio este calibrado, el
                  bloque entero avisa que es una referencia, no una orden de
                  compra. Desaparece solo cuando se carguen ratios reales. */}
              {suggestions.some(s => s.ratio_source === "default_sin_calibrar") && (
                <div className="flex items-start gap-2 mb-3 p-2 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-800">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>
                    Cantidades calculadas con ratios por defecto del sistema, no con recetas.
                    Sirven como orden de magnitud: no las uses todavia para decidir una compra.
                  </span>
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {suggestions.map(s => (
                  <div key={s.id}
                    className={`rounded-lg border p-3 text-center ${CATEGORY_COLOR[s.category] || "bg-slate-50 border-slate-200"}`}
                    data-testid={`suggestion-${s.category}`}
                  >
                    <div className="text-xs font-semibold capitalize mb-1">{s.category}</div>
                    <div className="text-2xl font-bold">{s.suggested_quantity}</div>
                    <div className="text-xs mt-0.5">{s.unit}</div>
                    {/* Fix WH32 (2026-09-07): la tarjeta decia "Conf. alta" al
                        lado de una cantidad calculada con un ratio que nadie
                        calibro. Ahora muestra el ratio y de donde sale, para que
                        el numero se lea como lo que es. */}
                    {s.ratio != null && (
                      <div className="text-[11px] text-slate-400 mt-1" title={s.rationale || ""}>
                        {s.ratio} {s.unit} por orden
                      </div>
                    )}
                    <div className={`text-xs mt-1 font-medium ${CONFIDENCE_COLOR[s.confidence] || "text-slate-400"}`}>
                      {CONFIDENCE_LABEL[s.confidence] || `Conf. ${s.confidence}`}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fix WH34 (2026-09-08): que preparar manana, platillo por platillo y
              hora por hora. A diferencia de las cinco categorias de arriba, esto
              NO usa ningun ratio: es el promedio ponderado de lo que realmente
              se vendio los ultimos ocho mismos dias de la semana, sacado del POS. */}
          {prodHora && !prodHora.insufficient_data && (prodHora.products || []).length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="produccion-por-hora">
              <div className="flex items-center gap-2 mb-1">
                <Package className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-semibold text-slate-700">Que preparar manana — hora por hora</span>
                <span className="ml-auto text-xs text-slate-400">
                  promedio de {prodHora.weeks_used} {prodHora.dow_name}s anteriores
                </span>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Unidades estimadas de cada platillo en cada franja, calculadas con la venta real del
                POS. Los {prodHora.products.length} de mayor volumen
                {prodHora.products_total > prodHora.products.length
                  ? ` de ${prodHora.products_total} con venta esperada` : ""}.
              </p>
              <div className="overflow-x-auto">
                <table className="text-xs w-full border-collapse">
                  <thead>
                    <tr className="text-slate-500">
                      <th className="text-left font-medium py-1 pr-3 sticky left-0 bg-white z-10">Platillo</th>
                      {prodHora.hours.map(h => (
                        <th key={h} className="font-medium px-1.5 py-1 text-right whitespace-nowrap">{h}h</th>
                      ))}
                      <th className="font-medium pl-3 py-1 text-right">Dia</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prodHora.products.map(p => (
                      <tr key={p.product_code} className="border-t border-slate-100">
                        <td className="py-1 pr-3 sticky left-0 bg-white z-10 whitespace-nowrap">
                          <span className="text-slate-800">{p.product_name}</span>
                          {p.category && <span className="text-slate-400 ml-1.5">{p.category}</span>}
                        </td>
                        {prodHora.hours.map(h => (
                          <td key={h} className={`px-1.5 py-1 text-right ${p.by_hour[h] ? "text-slate-700" : "text-slate-200"}`}>
                            {p.by_hour[h] || "·"}
                          </td>
                        ))}
                        <td className="pl-3 py-1 text-right font-semibold text-slate-900">{p.units_est}</td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-slate-200 font-semibold text-slate-700">
                      <td className="py-1 pr-3 sticky left-0 bg-white z-10">Total</td>
                      {prodHora.hours.map(h => (
                        <td key={h} className="px-1.5 py-1 text-right">{prodHora.totals_by_hour[h] || "·"}</td>
                      ))}
                      <td className="pl-3 py-1 text-right">{prodHora.units_total}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {prodHora && prodHora.insufficient_data && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-600">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{prodHora.message}</span>
            </div>
          )}

          {/* Fix WH41 (2026-09-09): INSUMOS DESDE LA RECETA REAL.
              Es lo que Claudia marcó como fundamental y lo que reemplaza, cuando
              esté contrastado contra la realidad, a las cinco categorías de
              arriba con su cartel "Sin calibrar". Por ahora conviven: aquéllas
              salen de un porcentaje escrito a mano, ésta de la receta de Wansoft.
              Se agrupa por departamento porque así es como se compra. */}
          {insumos && insumos.disponible && (insumos.insumos || []).length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3" data-testid="insumos-receta">
              <div className="flex items-start justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-slate-500" />
                  <span className="text-sm font-semibold text-slate-700">
                    Insumos para manana — desde la receta
                  </span>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-slate-900">
                    {fmtMXN(insumos.costo_total_estimado)}
                  </div>
                  <div className="text-[11px] text-slate-400">
                    {insumos.insumos.length} insumos · costo estimado
                  </div>
                </div>
              </div>

              {insumos.cobertura_pct < 100 && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5">
                  <button
                    onClick={() => setVerSinReceta(v => !v)}
                    className="w-full flex items-center justify-between gap-2 text-left"
                    data-testid="insumos-sin-receta-toggle"
                  >
                    <span className="flex items-start gap-2 text-xs text-amber-800">
                      <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span>
                        Cubre el <b>{insumos.cobertura_pct}%</b> de lo que se va a vender.
                        {" "}{(insumos.platillos_sin_receta || []).length} platillos
                        {" "}({insumos.unidades_sin_receta} unidades) no tienen receta cargada
                        y <b>no estan contados</b> en esta lista.
                      </span>
                    </span>
                    {verSinReceta
                      ? <ChevronUp className="w-4 h-4 text-amber-700 shrink-0" />
                      : <ChevronDown className="w-4 h-4 text-amber-700 shrink-0" />}
                  </button>
                  {verSinReceta && (
                    <div className="mt-2 pt-2 border-t border-amber-200 flex flex-wrap gap-1.5">
                      {(insumos.platillos_sin_receta || []).map((p, i) => (
                        <span key={i} className="text-[11px] bg-white border border-amber-200 rounded px-1.5 py-0.5 text-amber-900">
                          {p.platillo} <span className="text-amber-500">({p.unidades_est})</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-3">
                {Object.entries(
                  insumos.insumos.reduce((acc, i) => {
                    const d = i.departamento || "SIN DEPARTAMENTO";
                    (acc[d] = acc[d] || []).push(i);
                    return acc;
                  }, {})
                )
                  .sort((a, b) => b[1].reduce((s, x) => s + x.costo_estimado, 0)
                                - a[1].reduce((s, x) => s + x.costo_estimado, 0))
                  .map(([depto, filas]) => (
                    <div key={depto}>
                      <div className="flex items-center justify-between px-1 pb-1 border-b border-slate-100">
                        <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                          {depto}
                        </span>
                        <span className="text-[11px] text-slate-400">
                          {fmtMXN(filas.reduce((s, x) => s + x.costo_estimado, 0))}
                        </span>
                      </div>
                      {filas.map((i, k) => (
                        <div key={k} className="flex items-center justify-between gap-2 py-1.5 border-b border-slate-50 last:border-0">
                          <span className="text-sm text-slate-700 truncate">{i.insumo}</span>
                          <span className="flex items-baseline gap-2 shrink-0">
                            {i.rendimiento_pct < 100 && (
                              <span className="text-[10px] text-slate-400"
                                    title={`La receta pide ${i.cantidad_neta} ${i.unidad}. Con ${i.rendimiento_pct}% de rendimiento hay que comprar mas.`}>
                                merma {100 - i.rendimiento_pct}%
                              </span>
                            )}
                            <span className="text-sm font-semibold text-slate-900 tabular-nums">
                              {i.cantidad_a_comprar} <span className="text-xs font-normal text-slate-500">{i.unidad}</span>
                            </span>
                            <span className="text-xs text-slate-400 tabular-nums w-20 text-right">
                              {fmtMXN(i.costo_estimado)}
                            </span>
                          </span>
                        </div>
                      ))}
                    </div>
                  ))}
              </div>

              {(insumos.avisos || []).length > 0 && (
                <div className="text-[11px] text-slate-500 space-y-0.5 pt-1">
                  {insumos.avisos.map((a, i) => <div key={i}>· {a}</div>)}
                </div>
              )}
              <div className="text-[11px] text-slate-400 pt-1 border-t border-slate-100">
                Calculado con la receta de Wansoft cargada el{" "}
                {(insumos.recetario_actualizado_at || "").slice(0, 10)} y la venta
                proyectada por platillo. Las cantidades ya incluyen la merma de cada insumo.
              </div>
            </div>
          )}

          {insumos && !insumos.disponible && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-600"
                 data-testid="insumos-no-disponible">
              <Package className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{insumos.motivo}</span>
            </div>
          )}

          {/* Feriados y eventos */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <button
              onClick={() => setShowHolidays(p => !p)}
              className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-semibold text-slate-700">
                  Feriados y eventos ({holidays.length})
                </span>
              </div>
              <div className="flex items-center gap-2">
                {isAdmin && (
                  <button
                    onClick={e => { e.stopPropagation(); setEditingHoliday({}); }}
                    className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-[#FFDF00] text-slate-900 font-semibold hover:bg-yellow-300 transition-colors"
                    data-testid="new-holiday-btn"
                  >
                    <Plus className="w-3 h-3" /> Nuevo
                  </button>
                )}
                {showHolidays ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
              </div>
            </button>

            {showHolidays && (
              <div className="border-t border-slate-100">
                {holidays.length === 0 ? (
                  <p className="text-sm text-slate-400 p-4">Sin feriados registrados</p>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {holidays.map(h => (
                      <div key={h.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50">
                        <span className="text-xs font-mono text-slate-500 w-24 shrink-0">{h.date}</span>
                        <span className="flex-1 text-sm text-slate-800">{h.name}</span>
                        <span className="text-xs text-slate-400">{EVENT_TYPE_LABEL[h.type] || h.type}</span>
                        <span className="text-xs font-medium text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
                          x{h.multiplier}
                        </span>
                        {isAdmin && (
                          <>
                            <button onClick={() => setEditingHoliday(h)}
                              className="p-1 rounded hover:bg-slate-100 text-slate-400">
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            {h.type !== "feriado_nacional" && (
                              <button onClick={() => handleDeleteHoliday(h.id, h.name)}
                                className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-500">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {editingHoliday !== null && (
        <HolidayForm
          event={Object.keys(editingHoliday).length === 0 ? null : editingHoliday}
          branches={branches}
          onClose={() => setEditingHoliday(null)}
          onSaved={() => loadData(selectedBranch)}
        />
      )}
    </div>
  );
}
